define('meeting-modal/time-selector/time-selector',['require','api-access','api-access','time-zones','numeric','knockout','lodash','moment-timezone','i18next','jquery-ui/datepicker','knockout-jqueryui/datepicker'],function (require) {
    var userApi = require('api-access').user;
    var meetingsApi = require('api-access').meetings;
    var timeZones = require('time-zones');
    require('numeric');

    var ko = require('knockout');
    var _ = require('lodash');
    var moment = require('moment-timezone');
    var i18n = require('i18next');
    require('jquery-ui/datepicker');
    require('knockout-jqueryui/datepicker');

    /**
     * Contains logic for switching between scheduled and recurring meetings,
     * and setting the date and time if it is a scheduled meeting.
     *
     * If a meetingId is supplied, the module will assume edit behavior.
     * Do not supply the meetingId for scheduling a new meeting.
     */
    var TimeSelector = function (meetingId) {
        var self = this;

        // "scheduled" or "recurring"
        self.meetingType = ko.observable();

        // Array containing objects that map meeting type to the translated text:
        // Recurring -> Occurs multiple times, Scheduled -> Occurs once
        self.meetingTypes = ko.observableArray([
            {
                id: 'recurring', value: i18n.t('occurs.label') + ' ' +
                i18n.t('occurs.option.multipletimes')
            },
            {
                id: 'scheduled', value: i18n.t('occurs.label') + ' ' +
                i18n.t('occurs.option.once')
            }
        ]);

        self.datePickerDate = ko.observable();

        // The text input field for the start time.
        // Consumed by self.startTime to produce the actual complete time. For most scenarios, use that instead.
        self.startTimeInput = ko.observable().extend({
            validTime: {
                onlyIf: function () {
                    return self.meetingType() === 'scheduled';
                }
            }
        });

        self.am = ko.observable(i18n.t('time.am'));
        self.pm = ko.observable(i18n.t('time.pm'));
        // self.am or self.pm. This value populates the options in the dropdown.
        self.amOrPm = ko.observable();
        self.is12HourClock = ko.observable(i18n.t('web-technical.useAmPm') === 'true');

        /**
         * Contains a time zone object in the form specified by the time-zones module:
         *   {
         *       region: "Pacific/Tongatapu",
         *       zone: i18n.t('web-timezones.Pacific/Tongatapu'),
         *       offset: "+13:00"
         *   }
         */
        self.timeZone = ko.observable();
        self.timeZones = ko.observableArray(timeZones);
        self.isShowTimeZoneDropdown = ko.observable(false);

        /**
         * self.startTime is the final complete start time, and returns a moment object, including the
         * month, year, day, hours, minutes, and correct time zone offset.
         *
         * self.startTime is a "Knockout writable computed":
         *
         * When self.datePickerDate or self.startTimeInput are altered,
         * self.startTime will return the aforementioned moment object.
         *
         * When self.startTime is itself altered,
         * it will propogate the new values to self.startTimeInput and self.datePickerDate.
         * Currently, this is necessary to adjust the UI for the user when the the time zone is altered.
         */
        self.startTime = ko.pureComputed({
            read: function () {
                // Do not update when timeZone is updated,
                // .peek() enables self.startTime to read the value without subscribing to that observable.
                // A timeZone subscriber will manually write to self.startTime
                if (self.datePickerDate() && self.timeZone.peek() && self.startTimeInput()) {
                    var hours;
                    var minutes;
                    var year;
                    var month;
                    var date;
                    var startTime = moment().startOf('minute').tz(self.timeZone.peek().region);

                    if (self.is12HourClock()) {
                        var startTimeWithPeriod;
                        if (self.amOrPm() === self.am()) {
                            startTimeWithPeriod = self.startTimeInput() + ' AM';
                        } else {
                            startTimeWithPeriod = self.startTimeInput() + ' PM';
                        }
                        hours = moment(startTimeWithPeriod, 'h:mm A').hours();
                        minutes = moment(startTimeWithPeriod, 'h:mm A').minutes();
                    } else {
                        hours = moment(self.startTimeInput(), 'H:mm').hours();
                        minutes = moment(self.startTimeInput(), 'H:mm').minutes();
                    }

                    year = self.datePickerDate().getFullYear();
                    month = self.datePickerDate().getMonth();
                    date = self.datePickerDate().getDate();

                    startTime
                      .year(year)
                      .month(month)
                      .date(date)
                      .hours(hours)
                      .minutes(minutes);

                    return moment(startTime);
                }
                return undefined;
            },
            write: function (startTime) {
                if (self.is12HourClock()) {
                    self.startTimeInput(startTime.format('h:mm'));
                    if (startTime.format('a') === 'am') {
                        self.amOrPm(self.am());
                    } else {
                        self.amOrPm(self.pm());
                    }
                } else {
                    self.startTimeInput(startTime.format('H:mm'));
                }

                self.datePickerDate(new Date(startTime.year(), startTime.month(), startTime.date()));
            },
            owner: self
        }).extend({
            inTheFuture: {
                onlyIf: function () {
                    return self.startTimeInput.isValid() && self.meetingType() === 'scheduled';
                }
            }
        });

        /**
         * When the time zone changes, write to self.startTime the time with an offset adjustment.
         * self.timeZone will then propogate the new time to the UI elements.
         */
        self.timeZone.subscribe(function (timeZone) {
            if (self.startTime()) {
                self.startTime(moment(self.startTime()).tz(timeZone.region));
            }
        });

        self.duration = ko.observable().extend({
            required: {
                message: i18n.t('error.duration.min'),
                onlyIf: function () {
                    return self.meetingType() === 'scheduled';
                }
            },
            min: {
                params: 1,
                message: i18n.t('error.duration.min'),
                onlyIf: function () {
                    return self.meetingType() === 'scheduled';
                }
            },
            max: {
                params: 600,
                message: i18n.t('error.duration.max'),
                onlyIf: function () {
                    return self.meetingType() === 'scheduled';
                }
            }
        });

        /**
         * Build an array of durations:
         * [ { duration: '15', labeledDuration: '15 min' }, ... ]
         */
        var constructDurations = function () {
            var minutes = ['15', '30', '45', '60', '90', '120', '150', '180', '240',
                '300', '360', '420', '480'];
            var labeledDurations = _.map(minutes, function (minutesResponse) {
                // Longer than an hour, add an hour label: 90 min (1.5 hr)
                if (minutesResponse > 60) {
                    var hours = minutesResponse / 60;
                    return minutesResponse + ' ' + i18n.t('meetingHistory.tableText.minutes') +
                        ' (' + hours + ' ' + i18n.t('meetingHistory.tableText.hours') + ')';
                }
                return minutesResponse + ' ' + i18n.t('meetingHistory.tableText.minutes');
            });

            var durations = [];
            for (var i = 0; i < minutes.length; i++) {
                durations[i] = {
                    duration: minutes[i],
                    labeledDuration: labeledDurations[i]
                };
            }
            return durations;
        };
        self.durations = ko.observableArray(constructDurations());

        self.isDurationSelected = ko.observable(false);

        // Set the position of the non-interactive duration unit, "min", that floats in the input field.
        // Move further right if there are more characters in the duration field.
        self.durationUnitPosition = ko.pureComputed(function () {
            var pixels;
            if (self.duration()) {
                pixels = 25 + self.duration().length * 7.5;
            } else {
                pixels = 23;
            }
            return pixels + 'px';
        });

        /**
         * The final end time, simply the start time + duration.
         * Be sure to clone the start time first, since moment actions will alter the value.
         */
        self.endTime = ko.pureComputed(function () {
            if (self.duration() && self.startTime()) {
                return moment(self.startTime()).add(self.duration(), 'minutes');
            }
            return undefined;
        });

        self.isTimeZoneMismatched = ko.pureComputed(function () {
            if (self.startTime() && self.startTime.isValid()) {
                return moment().utcOffset() !== self.startTime().utcOffset();
            }
            return undefined;
        });

        self.timeZoneMismatchedText = ko.pureComputed(function () {
            var format;
            var time;

            if (self.is12HourClock()) {
                format = 'h:mm a';
            } else {
                format = 'H:mm';
            }

            time = moment(self.startTime())
                .utcOffset(moment().utcOffset())
                .format(format);

            return i18n.t('meetingtimezone.warning', time);
        });

        // List of scheduled, upcoming meetings that do not match the current meeting ID.
        self.filteredExistingMeetings = ko.observableArray();

        self.isMeetingOverlapping = ko.pureComputed(function () {
            if (self.startTime() && self.endTime() && self.filteredExistingMeetings().length > 0) {
                for (var i = 0; i < self.filteredExistingMeetings().length; i++) {
                    /**
                     * Date ranges overlap if:
                     * (StartTime1 <= EndTime2) and (StartTime2 <= EndTime1)
                     *
                     * In this case, Time1 is the current meeting, Time2 is the other existing meeting.
                     */
                    var meeting = self.filteredExistingMeetings()[i];
                    var startTime1 = self.startTime();
                    var endTime1 = self.endTime();
                    var startTime2 = moment(meeting.scheduledStartTime);
                    var endTime2 = moment(meeting.scheduledEndTime);

                    var isStartDateBeforeExistingEndDate = startTime1.diff(endTime2) <= 0;
                    var isExistingStartDateBeforeEndDate = startTime2.diff(endTime1) <= 0;

                    if (isStartDateBeforeExistingEndDate && isExistingStartDateBeforeEndDate) {
                        return true;
                    }
                }
            }
            return undefined;
        });

        // Loops through meetings and returns scheduled, upcoming meetings that do not match the current meeting ID.
        self.filterMeetings = function (meetings) {
            var filteredMeetings = _.filter(meetings, function (otherMeeting) {
                var isUsefulMeeting = otherMeeting.isUpcoming && otherMeeting.meetingType === 'scheduled';
                if (meetingId) {
                    isUsefulMeeting = isUsefulMeeting && (meetingId !== otherMeeting.meetingId);
                }
                return isUsefulMeeting;
            });
            return filteredMeetings;
        };

        // Set time to "now" rounded to the nearest hour forward. Set the duration to 30 minutes.
        var setTimeToDefaults = function () {
            /* eslint-disable newline-per-chained-call */
            self.startTime(moment().tz(self.timeZone().region).startOf('hour').add(1, 'hours'));
            /* eslint-enable */
            self.duration('30');
        };

        // Should contain all validated observables.
        // Parent modules can compose this object to determine if any errors exist.
        self.validation = ko.validatedObservable({
            startTimeInput: self.startTimeInput,
            duration: self.duration,
            startTime: self.startTime
        });

        /**
         * Returns relevant data, formatted for consumption by the API.
         */
        self.buildOptions = function () {
            var timeOptions = {};

            timeOptions.meetingType = self.meetingType();

            if (self.meetingType() === 'scheduled') {
                timeOptions.scheduledStartTime = self.startTime().format();
                timeOptions.scheduledEndTime = self.endTime().format();
                timeOptions.displayTimeZone = self.timeZone().region;
            }

            return timeOptions;
        };

        self.activate = function () {
            meetingsApi.forOrganizerFormatted().then(function (meetings) {
                self.filteredExistingMeetings(self.filterMeetings(meetings));
            });

            return userApi.settings().then(function (settings) {
                // The settings API returns the time zone in region form.
                // Search the time zone list for the correct entry by region.
                // If the account time zone doesn't match an entry from our list, default to PST.
                var timeZone = _.where(timeZones, {region: settings.timeZone})[0];
                if (timeZone) {
                    self.timeZone(timeZone);
                } else {
                    self.timeZone(_.where(timeZones, {region: 'America/Los_Angeles'})[0]);
                }

                if (meetingId) {
                    return meetingsApi.byIdFormatted(meetingId).then(function (meeting) {
                        self.meetingType(meeting.meetingType);
                        // For scheduled meetings, grab the correct time and duration as expected.
                        // For recurring meetings, set the time and duration to defaults, in case the user switches.
                        if (meeting.meetingType === 'scheduled') {
                            self.startTime(moment(meeting.scheduledStartTime).tz(self.timeZone().region));
                            self.duration(moment(meeting.scheduledEndTime).diff(self.startTime(), 'minutes'));
                        } else {
                            setTimeToDefaults();
                        }
                    });
                }
                self.meetingType('scheduled');
                setTimeToDefaults();
                return undefined;
            });
        };
    };

    return TimeSelector;
});

